import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, setToken, getToken, adminMe } from "@/lib/api";
import { TafraLogo } from "@/components/site/Header";
import ThemeToggle from "@/components/site/ThemeToggle";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = getToken();
    if (!t) return;
    adminMe()
      .then(() => navigate("/admin/dashboard", { replace: true }))
      .catch(() => localStorage.removeItem("tafra_admin_token"));
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await adminLogin(email.trim().toLowerCase(), password);
      setToken(res.access_token);
      toast.success("Signed in.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-6"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <div className="absolute inset-0 energy-grid-bg opacity-30" />
      <div className="absolute inset-0 radial-fade opacity-70" />
      <div className="absolute inset-0 radial-crimson opacity-60" />

      <div className="absolute top-6 right-6 z-10">
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-8 transition-colors"
          style={{ color: "var(--text-muted)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-blue)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
          data-testid="back-to-site-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to site
        </Link>

        <div
          className="relative p-8 lg:p-10 btn-sharp backdrop-blur-xl"
          style={{
            background: "var(--surface-elev)",
            border: "1px solid var(--border)",
          }}
        >
          <span
            className="absolute -top-px left-10 right-10 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
            }}
          />
          <span
            className="absolute -bottom-px left-10 right-10 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--accent-crimson), transparent)",
            }}
          />

          <div className="flex items-center gap-3">
            <TafraLogo size={40} />
            <div className="leading-none">
              <span
                className="font-display text-[14px] tracking-[0.28em] font-medium"
                style={{ color: "var(--text)" }}
              >
                TAFRA
              </span>
              <span
                className="block font-display text-[10px] tracking-[0.4em] mt-1"
                style={{ color: "var(--accent-blue)" }}
              >
                ENERGY · ADMIN
              </span>
            </div>
          </div>

          <h1
            className="mt-8 font-display text-2xl font-medium"
            style={{ color: "var(--text)" }}
          >
            Sign in
          </h1>

          <form
            onSubmit={onSubmit}
            className="mt-8 space-y-4"
            data-testid="admin-login-form"
          >
            <div>
              <label
                className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                style={{ color: "var(--text-faint)" }}
              >
                Admin email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
                className="w-full btn-sharp px-4 py-3 focus:outline-none"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  color: "var(--text)",
                }}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label
                className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                style={{ color: "var(--text-faint)" }}
              >
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
                className="w-full btn-sharp px-4 py-3 focus:outline-none"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  color: "var(--text)",
                }}
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold btn-sharp transition-all disabled:opacity-60"
              style={{
                background: "var(--accent-blue)",
                color: "var(--bg)",
                boxShadow: "0 0 18px var(--accent-blue-glow)",
              }}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Sign in
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
